/**
 * deckungsbeitragRollup (#1781) — pure helpers behind the ranked margin
 * table. Facts arrive one-per-(crop_cycle, period) (deterministic uuid5
 * ids, incl. #1720 establishment-only thin facts), so the period selector
 * is a query-time rollup over the already-fetched rows (design principle
 * 10: period is never an identity) — switching periods re-filters
 * client-side, no refetch.
 */
import { wjLabel, wjStartYear } from '../liquiditaetsplanung/monthLensItems';
import { db1PerBasis, db1PerBasisForGroup, type MarginBasis } from './basis';
import { kulturGroupLabel } from './rowLabel';
import { classifyAnalysisUnit, classifyGroup, classifyGroupSums, classifyRow } from './rowQuality';
import type {
  AnalysisUnitRow,
  DeckungsbeitragRow,
  DirectCosts,
  KulturChild,
  KulturRollupRow,
} from './types';

// #2773 A — the revenueless predicate moved into the row classifier
// (``rowQuality.ts``, its only non-test caller); re-exported here so existing
// importers and the truth-table tests keep their import path.
export { isRevenuelessRow } from './rowQuality';

/** Sentinel period id for rows with a blank crop_year — their money is real, never drop them. */
export const OHNE_JAHR = 'ohne-jahr';

/**
 * #2773 C — does this row/group leave the ranking's competitive population? Only
 * ``not_applicable`` does: a non-marketable Kultur has no Deckungsbeitrag to compare,
 * so ranking it against real crops is a category error regardless of the € it shows.
 * ``pending``/``untrustworthy`` rows still rank normally when revealed — their number
 * is incomplete or doubtful, not meaningless.
 */
const parksLast = (row: Parameters<typeof classifyRow>[0]): boolean =>
  classifyRow(row).state === 'not_applicable';

/** The same rule at group grain — ``every(member)`` propagation, so a MIXED group ranks normally. */
const groupParksLast = (group: Parameters<typeof classifyGroup>[0]): boolean =>
  classifyGroup(group).state === 'not_applicable';

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * The current Wirtschaftsjahr as its START calendar year, given ``wjStartMonth``
 * (#2268). "Current WJ" is the WJ containing ``today`` — NOT the calendar year:
 * a July farm before July is still in last year's WJ. Reuses ``wjStartYear`` (the
 * same formula liquiditaetsplanung labels its WJ column with) so the field panel
 * and the Deckungsbeitrag view can never disagree about which WJ is current.
 * ``today`` defaults to now but is injectable so callers/tests stay clock-stable.
 */
export function currentWirtschaftsjahr(wjStartMonth: number, today: Date = new Date()): number {
  const ym = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  return wjStartYear(ym, wjStartMonth);
}

/**
 * Drop rows whose ``crop_year`` (period) lies BEYOND the current Wirtschaftsjahr
 * (#2268) — a forward-planting cycle in a not-yet-started WJ carries structurally
 * zero Leistung and must never surface as a period tab / deep-link target. Applied
 * ONCE at the page/hook boundary so every downstream consumer (period tabs,
 * rowsForPeriod, periodLensItems, summary, chart, rollup) is consistent and a
 * ``?period=2027`` deep-link yields nothing.
 *
 * KEEPS the current, in-progress (partial) WJ — the boundary is strictly ``>``,
 * derived from ``wjStartMonth`` so it shifts on the fiscal-year start, not on 1 Jan.
 * KEEPS null (OHNE_JAHR) and any non-numeric period — real money with no year to
 * compare. Non-mutating.
 */
export function withoutFutureWirtschaftsjahre(
  rows: DeckungsbeitragRow[],
  wjStartMonth: number,
  today: Date = new Date(),
): DeckungsbeitragRow[] {
  const boundary = currentWirtschaftsjahr(wjStartMonth, today);
  return rows.filter((row) => {
    if (row.period === null) return true; // OHNE_JAHR — never drop
    const year = Number(row.period);
    // A non-numeric / non-clean-integer period has no year to compare → keep it
    // (mirrors periodLabel's pass-through of a non-year period).
    if (!Number.isInteger(year) || row.period.trim() !== String(year)) return true;
    return year <= boundary;
  });
}

/**
 * Collapse duplicate ``derived_fact_id`` rows (first occurrence wins).
 * Ids are deterministic per (tenant, crop_cycle, period), so identity
 * dedupe is safe and guarantees a (cc, period) pair is never double-counted.
 */
export function dedupeByFactId(rows: DeckungsbeitragRow[]): DeckungsbeitragRow[] {
  const seen = new Set<string>();
  const out: DeckungsbeitragRow[] = [];
  for (const row of rows) {
    if (seen.has(row.derived_fact_id)) continue;
    seen.add(row.derived_fact_id);
    out.push(row);
  }
  return out;
}

/** Distinct periods, newest first; the null bucket (as ``OHNE_JAHR``) always last. */
export function periodsOf(rows: DeckungsbeitragRow[]): string[] {
  const set = new Set<string>();
  let hasNull = false;
  for (const row of rows) {
    if (row.period === null) hasNull = true;
    else set.add(row.period);
  }
  const sorted = [...set].sort((a, b) => b.localeCompare(a, 'de'));
  if (hasNull) sorted.push(OHNE_JAHR);
  return sorted;
}

/** Rows of one selected period; ``OHNE_JAHR`` selects the null-period rows. */
export function rowsForPeriod(rows: DeckungsbeitragRow[], periodId: string): DeckungsbeitragRow[] {
  return rows.filter((row) => (periodId === OHNE_JAHR ? row.period === null : row.period === periodId));
}

/**
 * Ranking order for a chosen Bezugsgröße (#2304): ``db1PerBasis`` descending,
 * ALL rows without that basis quantity last (an unknown denominator must not
 * rank as 0 €/X); ties break db1 desc, then crop asc. Non-mutating; never
 * compares against NaN (mappers + basisQuantity guarantee null-or-finite).
 */
export function rankByDb1PerBasis(rows: DeckungsbeitragRow[], basis: MarginBasis): DeckungsbeitragRow[] {
  return [...rows].sort((a, b) => {
    // #2773 C — non-marketable land parks BELOW everything, including an unknown
    // denominator. A Kultur that has no Deckungsbeitrag by definition cannot be
    // "the best Kultur", and it topping the ranking is the issue's headline
    // complaint. Applied here (not only at the filter) because the toggle reveals
    // these rows, and a revealed Grünland must not out-rank a real crop.
    const na = parksLast(a);
    if (na !== parksLast(b)) return na ? 1 : -1;
    const av = db1PerBasis(a, basis);
    const bv = db1PerBasis(b, basis);
    if ((av === null) !== (bv === null)) return av === null ? 1 : -1;
    if (av !== null && bv !== null && av !== bv) return bv - av;
    if (a.db1 !== b.db1) return b.db1 - a.db1;
    return a.crop.localeCompare(b.crop, 'de');
  });
}

/** The ``ha`` special case — the historical ranking, byte-identical to before #2304. */
export function rankByDb1PerHa(rows: DeckungsbeitragRow[]): DeckungsbeitragRow[] {
  return rankByDb1PerBasis(rows, 'ha');
}

/**
 * The seven-bucket Direktkosten total the table renders (and now also sorts
 * on). Lives here — not inline in the component — so the sorted value and
 * the rendered cell can never drift (a codex pre-emption for #2221).
 *
 * HOF-518 added ``land``. Every bucket the engine subtracts to reach DB1 must be
 * summed here, because the cell this feeds is rendered in the same table ROW as
 * that DB1 — omitting one makes the table disagree with itself, and makes the
 * Direktkosten sort rank a land-heavy Kultur as if its rent were free.
 */
export const directCostTotal = (c: DirectCosts): number =>
  c.saatgut +
  c.duenger +
  c.psm +
  c.loehne +
  c.maschinen +
  c.sonstige +
  c.land +
  // #4172 wave 2 — the five labour buckets, for the reason the docstring above gives about
  // ``land``: every bucket the engine subtracts to reach DB1 must be summed here, or the
  // Direktkosten cell disagrees with the DB1 printed in the same table row. On a wiberry
  // tenant the picking wage is the single largest of them, so omitting it would rank a
  // labour-heavy Kultur as if its harvest crew were free.
  c.pflueck +
  c.nebenkosten +
  c.verpackung +
  c.kuehlung +
  c.pflanzung;

export type MarginSortDir = 'asc' | 'desc';

const sumDirectCosts = (members: DeckungsbeitragRow[]): DirectCosts => ({
  saatgut: round2(members.reduce((acc, m) => acc + m.direct_costs.saatgut, 0)),
  duenger: round2(members.reduce((acc, m) => acc + m.direct_costs.duenger, 0)),
  psm: round2(members.reduce((acc, m) => acc + m.direct_costs.psm, 0)),
  loehne: round2(members.reduce((acc, m) => acc + m.direct_costs.loehne, 0)),
  maschinen: round2(members.reduce((acc, m) => acc + m.direct_costs.maschinen, 0)),
  sonstige: round2(members.reduce((acc, m) => acc + m.direct_costs.sonstige, 0)),
  // HOF-518 — Landkosten sums like every other bucket. A rolled-up Kultur's land cost is
  // the Σ over its cycles, which is what the €-total column and the per-ha quotient below
  // are computed against; a constant here would understate the group by the whole rent.
  land: round2(members.reduce((acc, m) => acc + m.direct_costs.land, 0)),
  // #4172 wave 2 — and so do the five labour buckets, for exactly that reason.
  pflueck: round2(members.reduce((acc, m) => acc + m.direct_costs.pflueck, 0)),
  nebenkosten: round2(members.reduce((acc, m) => acc + m.direct_costs.nebenkosten, 0)),
  verpackung: round2(members.reduce((acc, m) => acc + m.direct_costs.verpackung, 0)),
  kuehlung: round2(members.reduce((acc, m) => acc + m.direct_costs.kuehlung, 0)),
  pflanzung: round2(members.reduce((acc, m) => acc + m.direct_costs.pflanzung, 0)),
  // Layer C — sums like every bucket. NOT in ``directCostTotal`` above: it is Investition,
  // not a Direktkost, and that cell already leaves Etablierung (its layer-C sibling) out.
  investition: round2(members.reduce((acc, m) => acc + m.direct_costs.investition, 0)),
});

/** Blank-crop display name — one string for the table, the sort and the Kultur rollup. */
export const UNBENANNTE_KULTUR = 'Unbenannte Kultur';

/**
 * The stable Kultur+Sorte group key (#2302): RAW crop name + NUL + variety
 * (empty when null). It is the chart bar id in Kultur mode and the tree
 * table's expansion key — chart and table address the SAME groups. The
 * blank-crop → "Unbenannte Kultur" fallback is applied ONLY when rendering
 * (the rollup's ``crop`` field + ``kulturGroupLabel``), never in the key:
 * keying on the display name would silently merge a blank crop with a farm's
 * real crop literally named "Unbenannte Kultur" and corrupt their sums.
 */
export function kulturGroupKey(crop: string, variety: string | null): string {
  return `${crop ?? ''}\u0000${variety ?? ''}`;
}

/**
 * Group the (already period-filtered) rows by (Kultur, Sorte) — Level 1 of
 * the tree table AND the chart's default aggregation (#2302). Honesty rules:
 * ``area_ha`` = Σ only when EVERY member
 * carries an area; ``db1_per_ha`` = round2(Σdb1/Σarea) — the area-weighted
 * quotient, NEVER the mean of member per-ha values (unequal areas would
 * misstate the Kultur, the issue's called-out trap); the incomplete flag
 * propagates if ANY member carries it. ``pending`` is the row classifier's
 * sums-grain application (``classifyGroupSums``, #2773 A) over the group
 * sums — one booked member un-pends the whole group (status quo). Members
 * come back pre-ranked for Level 2; groups rank by ``db1_per_ha`` desc
 * nulls-last (db1 desc, then label asc tie-breaks) so chart and table share
 * one order.
 *
 * #2304 note: the per-ha quotient lives HERE (single helper) so a future
 * Bezugsgröße switcher only swaps the denominator.
 */
export function rollupByKultur(rows: DeckungsbeitragRow[]): KulturRollupRow[] {
  const groups = new Map<string, DeckungsbeitragRow[]>();
  for (const row of rows) {
    const key = kulturGroupKey(row.crop, row.variety);
    const bucket = groups.get(key) ?? [];
    bucket.push(row);
    groups.set(key, bucket);
  }
  const out = [...groups.entries()].map(([key, members]): KulturRollupRow => {
    const sum = (pick: (m: DeckungsbeitragRow) => number): number =>
      round2(members.reduce((acc, m) => acc + pick(m), 0));
    const fullAreaCoverage = members.every((m) => m.area_ha != null);
    const areaHa = fullAreaCoverage ? round2(members.reduce((acc, m) => acc + (m.area_ha ?? 0), 0)) : null;
    const db1 = sum((m) => m.db1);
    const leistung = sum((m) => m.leistung);
    const ranked = rankByDb1PerHa(members);
    return {
      group_key: key,
      crop: members[0].crop || UNBENANNTE_KULTUR,
      variety: members[0].variety,
      memberCount: members.length,
      area_ha: areaHa,
      leistung,
      direktzahlung: sum((m) => m.direktzahlung),
      direct_costs: sumDirectCosts(members),
      establishment_amortised: sum((m) => m.establishment_amortised),
      db1,
      db2: sum((m) => m.db2),
      db1_per_ha: areaHa ? round2(db1 / areaHa) : null,
      incomplete_on_shared_overhead: members.some((m) => m.incomplete_on_shared_overhead),
      // #2695 E — the group's Σ Leistung silently BLENDS booked revenue with price-book
      // estimates the moment one member falls back, so the flag has to travel with the sum.
      // ``some``, not ``every``, for the same reason ``incomplete_on_shared_overhead`` uses it:
      // one un-booked member already makes the displayed € something other than money received.
      leistung_estimated: members.some((m) => m.leistung_estimated),
      // HOF-21 — same fold, same reason. The members handed to this rollup have already
      // been through ``applyValuationMode``, so this asks "is any member SHOWING a
      // valuation", not "could any member be valued" — which is the question the badge
      // beside the Σ has to answer.
      leistung_incomplete: members.some((m) => m.leistung_incomplete),
      pending: classifyGroupSums({ leistung, db1 }).state === 'pending',
      members: ranked,
      // HOF-195 — the flat, one-child-per-member Level 2 this table has always rendered.
      // ``withAnalysisUnits`` rewrites it where a tenant opted a Hauptschlag in; where none
      // did, this IS the shipped behaviour, so „absent config => status quo" needs no branch.
      children: ranked.map(leafChild),
    };
  });
  // The ranked answer order, mirroring rankByDb1PerHa at group grain:
  // non-marketable groups last (#2773 C), then per-ha desc, null-per-ha groups
  // last, ties db1 desc then label asc.
  return out.sort((a, b) => {
    const na = groupParksLast(a);
    if (na !== groupParksLast(b)) return na ? 1 : -1;
    const av = a.db1_per_ha;
    const bv = b.db1_per_ha;
    if ((av === null) !== (bv === null)) return av === null ? 1 : -1;
    if (av !== null && bv !== null && av !== bv) return bv - av;
    if (a.db1 !== b.db1) return b.db1 - a.db1;
    return kulturGroupLabel(a).localeCompare(kulturGroupLabel(b), 'de');
  });
}

/**
 * HOF-195 — the stable key of one Hauptschlag analysis unit: the Kultur+Sorte group key,
 * NUL, the raw ``hauptschlag`` string. Scoped to the group on purpose — the same
 * Hauptschlag legitimately appears under two Sorten (WF's 2025 Lange Striche carries two
 * Hademar and two Karima cycles) and those must never share an expansion key, let alone
 * a Sum. NUL for the same reason ``kulturGroupKey`` uses it: no farm string contains it,
 * so no pair of components can collide by concatenation.
 */
export function analysisUnitKey(groupKey: string, hauptschlag: string): string {
  return `${groupKey}\u0000${hauptschlag}`;
}

/**
 * HOF-195 — how many blocks a Hauptschlag needs before it earns a rollup level.
 *
 * Two, because a unit over ONE block is that block: the Sum, the denominator and
 * therefore every re-derived verdict are identical, so the level buys nothing and costs a
 * click. It is a display threshold and nothing else — the curated opt-in list still says
 * which Hauptschläge are analysis units; this only says when drawing one is worth a row.
 */
export const ANALYSIS_UNIT_MIN_MEMBERS = 2;

/** One Level-2 leaf child, keyed exactly as the pre-HOF-195 Level 2 keyed its rows. */
const leafChild = (row: DeckungsbeitragRow): KulturChild => ({
  kind: 'row',
  key: row.derived_fact_id,
  row,
});

/** The same parking rule as every other grain, at unit grain (``every(member)`` → not_applicable). */
const unitParksLast = (unit: AnalysisUnitRow): boolean =>
  classifyAnalysisUnit(unit).state === 'not_applicable';

/** The comparable per-basis value of one Level-2 child — a unit aggregates, a leaf reads its own. */
const childPerBasis = (child: KulturChild, basis: MarginBasis): number | null =>
  child.kind === 'unit' ? db1PerBasisForGroup(child.unit, basis) : db1PerBasis(child.row, basis);

const childDb1 = (child: KulturChild): number =>
  child.kind === 'unit' ? child.unit.db1 : child.row.db1;

const childParksLast = (child: KulturChild): boolean =>
  child.kind === 'unit' ? unitParksLast(child.unit) : parksLast(child.row);

/** The tie-break label — the unit's Hauptschlag, or the leaf's crop (what ``rankByDb1PerBasis`` uses). */
const childLabel = (child: KulturChild): string =>
  child.kind === 'unit' ? child.unit.hauptschlag : child.row.crop;

/**
 * HOF-195 — rank the Level-2 children (units and loose leaves together) by exactly the
 * rule ``rankByDb1PerBasis`` applies to leaves: non-marketable parks last, then per-basis
 * desc, then an unknown denominator last, ties db1 desc then label asc. A unit ranks
 * among its sibling blocks rather than above them — it is one more thing on the level,
 * not a new class of thing. A unit's own blocks are re-ranked for the same basis.
 * Non-mutating.
 */
export function rankKulturChildren(children: KulturChild[], basis: MarginBasis): KulturChild[] {
  const reranked = children.map((child): KulturChild =>
    child.kind === 'unit'
      ? { ...child, unit: { ...child.unit, members: rankByDb1PerBasis(child.unit.members, basis) } }
      : child,
  );
  return reranked.sort((a, b) => {
    const na = childParksLast(a);
    if (na !== childParksLast(b)) return na ? 1 : -1;
    const av = childPerBasis(a, basis);
    const bv = childPerBasis(b, basis);
    if ((av === null) !== (bv === null)) return av === null ? 1 : -1;
    if (av !== null && bv !== null && av !== bv) return bv - av;
    const ad = childDb1(a);
    const bd = childDb1(b);
    if (ad !== bd) return bd - ad;
    return childLabel(a).localeCompare(childLabel(b), 'de');
  });
}

/**
 * HOF-195 — the Sum at the united grain, over blocks already known to share one
 * Kultur+Sorte and one period.
 *
 * Every honesty rule is ``rollupByKultur``'s, deliberately: ``area_ha`` is a Sum only
 * when EVERY member carries one, and ``db1_per_ha`` is ``round2(Sum db1 / Sum area)`` —
 * the AREA-WEIGHTED quotient, never the mean of the members' per-ha values. The blocks
 * have unequal areas (0,44 to 0,71 ha on WF), which is precisely the case where the mean
 * misstates the unit; taking it would reintroduce, one level down, the trap #2302 named.
 */
function rollupAnalysisUnit(
  unitKey: string,
  hauptschlag: string,
  members: DeckungsbeitragRow[],
): AnalysisUnitRow {
  const sum = (pick: (m: DeckungsbeitragRow) => number): number =>
    round2(members.reduce((acc, m) => acc + pick(m), 0));
  const fullAreaCoverage = members.every((m) => m.area_ha != null);
  const areaHa = fullAreaCoverage ? round2(members.reduce((acc, m) => acc + (m.area_ha ?? 0), 0)) : null;
  const db1 = sum((m) => m.db1);
  return {
    unit_key: unitKey,
    hauptschlag,
    memberCount: members.length,
    area_ha: areaHa,
    leistung: sum((m) => m.leistung),
    direktzahlung: sum((m) => m.direktzahlung),
    direct_costs: sumDirectCosts(members),
    establishment_amortised: sum((m) => m.establishment_amortised),
    db1,
    db2: sum((m) => m.db2),
    db1_per_ha: areaHa ? round2(db1 / areaHa) : null,
    incomplete_on_shared_overhead: members.some((m) => m.incomplete_on_shared_overhead),
    leistung_estimated: members.some((m) => m.leistung_estimated),
    leistung_incomplete: members.some((m) => m.leistung_incomplete),
    members,
  };
}

/**
 * HOF-195 — fold each group's Level 2 into Hauptschlag analysis units, for the
 * Hauptschläge the tenant has opted in.
 *
 * **What it does NOT do.** It touches no group Sum, no ``members`` array and no Level-1
 * ranking: it rewrites ``children`` and nothing else. That is why the Kultur grain is
 * byte-identical with and without the rollup level — a Sum is invariant to intermediate
 * grouping, and here there is not even an intermediate Sum in the path.
 *
 * **Why an opt-in list and not the raw string.** ``field.hauptschlag`` demonstrably
 * over-groups on WF today: under ``2- Autobahn Tunnel`` the curated crosswalk carries four
 * separate register fields of one Kultur AND one Sorte, and Max asked only for Lange
 * Striche. Grouping on the raw value would invent the answer to a question he still owes
 * an email about. Absent list => every child is a leaf => today's page, exactly.
 *
 * Grouping happens strictly INSIDE one Kultur+Sorte group, and the rows handed here are
 * already one period's, so no unit can ever blend two Sorten or two Erntejahre — WF's
 * 2025 Lange Striche (two Hademar, two Karima) yields two units, never one blended row.
 */
export function withAnalysisUnits(
  groups: KulturRollupRow[],
  analysisUnits: readonly string[],
  basis: MarginBasis,
): KulturRollupRow[] {
  const opted = new Set(analysisUnits);
  if (opted.size === 0) return groups;
  return groups.map((group) => {
    const buckets = new Map<string, DeckungsbeitragRow[]>();
    const loose: DeckungsbeitragRow[] = [];
    for (const member of group.members) {
      const hauptschlag = member.hauptschlag;
      if (hauptschlag === null || !opted.has(hauptschlag)) {
        loose.push(member);
        continue;
      }
      const bucket = buckets.get(hauptschlag) ?? [];
      bucket.push(member);
      buckets.set(hauptschlag, bucket);
    }
    const children: KulturChild[] = loose.map(leafChild);
    for (const [hauptschlag, members] of buckets) {
      if (members.length < ANALYSIS_UNIT_MIN_MEMBERS) {
        children.push(...members.map(leafChild));
        continue;
      }
      const unitKey = analysisUnitKey(group.group_key, hauptschlag);
      children.push({ kind: 'unit', key: unitKey, unit: rollupAnalysisUnit(unitKey, hauptschlag, members) });
    }
    return { ...group, children: rankKulturChildren(children, basis) };
  });
}

/**
 * Re-rank the (basis-agnostic) Kultur rollups for a chosen Bezugsgröße (#2304):
 * groups order by ``db1PerBasisForGroup`` desc, groups without that basis last
 * (db1 desc, then label asc tie-breaks — mirroring ``rollupByKultur``'s ``ha``
 * order); each group's members are re-ranked by the same basis so the Level-2
 * leaves read top-to-worst on the selected denominator too. For ``ha`` this is a
 * no-op over the rollup's own order. Non-mutating (members replaced, not sorted
 * in place).
 */
export function rankKulturByBasis(groups: KulturRollupRow[], basis: MarginBasis): KulturRollupRow[] {
  const withMembers = groups.map((group) => ({
    ...group,
    members: rankByDb1PerBasis(group.members, basis),
    // HOF-195 — Level 2 follows the same Bezugsgroesse as Level 1 and Level 3. Any analysis
    // unit already sitting in ``children`` re-ranks its own blocks too, so a basis switch
    // reorders all three levels in one pass and none of them can disagree.
    children: rankKulturChildren(group.children, basis),
  }));
  return withMembers.sort((a, b) => {
    // #2773 C — same parking rule as the row ranking, so chart order == table order.
    const na = groupParksLast(a);
    if (na !== groupParksLast(b)) return na ? 1 : -1;
    const av = db1PerBasisForGroup(a, basis);
    const bv = db1PerBasisForGroup(b, basis);
    if ((av === null) !== (bv === null)) return av === null ? 1 : -1;
    if (av !== null && bv !== null && av !== bv) return bv - av;
    if (a.db1 !== b.db1) return b.db1 - a.db1;
    return kulturGroupLabel(a).localeCompare(kulturGroupLabel(b), 'de');
  });
}

/**
 * The sortable Level-1 (Kultur) columns of the tree table (#2302, ex-#2221).
 * ``db1_per_ha`` is the basis-relative ranking column (#2304): its value is
 * ``db1PerBasisForGroup`` for the active basis, not literally per-ha.
 */
export type KulturSortKey = 'crop' | 'area_ha' | 'leistung' | 'direktkosten' | 'db1_per_ha';

/** Numeric Level-1 columns start `desc` (big money first); the Kultur column starts `asc`. */
export const KULTUR_NUMERIC_KEYS: ReadonlySet<KulturSortKey> = new Set<KulturSortKey>([
  'area_ha',
  'leistung',
  'direktkosten',
  'db1_per_ha',
]);

/** The comparable value of one Level-1 column; missing values return null to park last. */
function kulturSortValue(
  group: KulturRollupRow,
  key: KulturSortKey,
  basis: MarginBasis,
): string | number | null {
  switch (key) {
    case 'crop':
      return kulturGroupLabel(group);
    case 'area_ha':
      return group.area_ha;
    case 'leistung':
      return group.leistung;
    case 'direktkosten':
      return directCostTotal(group.direct_costs);
    case 'db1_per_ha':
      // The ranking column follows the active Bezugsgröße (#2304); ``ha`` is the
      // stored per-ha value, so an omitted ``basis`` keeps the pre-#2304 sort.
      return db1PerBasisForGroup(group, basis);
  }
}

/**
 * Interactive column sort over the Level-1 rollup rows (#2302 — the #2221
 * behaviour, retargeted at Kultur grain). Applied over the ALREADY-ranked
 * groups, so ties inherit the rollup order via sort stability. Missing
 * values park LAST in BOTH directions (the null test sits outside the
 * direction flip); strings collate `de`. Non-mutating.
 */
export function sortKulturRollups(
  groups: KulturRollupRow[],
  key: KulturSortKey,
  dir: MarginSortDir,
  basis: MarginBasis = 'ha',
): KulturRollupRow[] {
  return [...groups].sort((a, b) => {
    const av = kulturSortValue(a, key, basis);
    const bv = kulturSortValue(b, key, basis);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    if (typeof av === 'string' && typeof bv === 'string') {
      const cmp = av.localeCompare(bv, 'de');
      return dir === 'asc' ? cmp : -cmp;
    }
    const cmp = (av as number) - (bv as number);
    return dir === 'asc' ? cmp : -cmp;
  });
}

/**
 * Human label for a period id. ``period`` = crop_year — the primary label
 * stays the raw year (don't silently relabel a year as a WJ); the WJ hint
 * is secondary and only shown for non-calendar Wirtschaftsjahre
 * (``wjStartMonth !== 1``). Null / sentinel → "Ohne Jahr"; a non-numeric
 * period passes through untouched.
 */
export function periodLabel(period: string | null, wjStartMonth: number): string {
  if (period === null || period === OHNE_JAHR) return 'Ohne Jahr';
  const year = Number(period);
  if (!Number.isInteger(year) || period.trim() !== String(year)) return period;
  if (wjStartMonth === 1) return period;
  return `${period} (${wjLabel(year)})`;
}
